import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ChevronDown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import RichTextEditor from "@/components/RichTextEditor";
import AdminHint from "@/components/admin/AdminHint";

export default function ContentPanel() {
  const { reload } = useSite();
  const [items, setItems] = useState([]);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/snippets");
    setItems(data);
    const v = {};
    data.forEach((it) => { v[it.key] = it.value; });
    setValues(v);
  };

  useEffect(() => { load(); }, []);

  const groups = items.reduce((acc, it) => {
    (acc[it.group] = acc[it.group] || []).push(it);
    return acc;
  }, {});
  const groupNames = Object.keys(groups);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/snippets", { items: values });
      await reload();
      toast.success("Text saved — your changes are now live.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <AdminHint>
        This is where you edit the <strong>wording</strong> across your whole site. Open a section below, type your
        changes into any box, then press <strong>Save all changes</strong> — your words appear on the live site
        straight away. To change the main text of a page instead, use the <strong>Pages</strong> tab.
      </AdminHint>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="font-body text-brand-slate">Edit any wording on the site. Open a section, make your changes, then Save.</p>
        <button onClick={save} disabled={busy} data-testid="save-content-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-forest text-white px-5 py-2.5 font-body text-sm hover:bg-brand-forest-dark disabled:opacity-60">
          <Save size={16} strokeWidth={1.5} /> {busy ? "Saving…" : "Save all changes"}
        </button>
      </div>

      <div className="space-y-3">
        {groupNames.map((g) => {
          const isOpen = openGroup === g;
          return (
            <div key={g} className="rounded-2xl border border-brand-line bg-white overflow-hidden">
              <button
                onClick={() => setOpenGroup(isOpen ? null : g)}
                data-testid={`content-group-${g.toLowerCase().replace(/\s+/g, "-")}`}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-brand-accent/40"
              >
                <span className="font-heading text-xl text-brand-ink">{g}</span>
                <ChevronDown size={18} strokeWidth={1.5} className={`text-brand-slate transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-5 pb-6 pt-1 space-y-6 border-t border-brand-line">
                  {groups[g].map((it) => (
                    <div key={it.key} data-testid={`content-field-${it.key}`}>
                      <label className="font-body text-sm text-brand-slate block mb-2">{it.label}</label>
                      <RichTextEditor
                        key={it.key}
                        value={values[it.key]}
                        onChange={(html) => setValues((prev) => ({ ...prev, [it.key]: html }))}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
